import { Card, CardContent } from '@/components/ui/card'
import { BellRing, FileText, MessagesSquare, ShieldCheck, Clock3, ListChecks, LucideIcon } from 'lucide-react'

interface TrustCard {
  icon: LucideIcon
  title: string
  oneLiner: string
  proof: string
  isHighlighted?: boolean
}

const trustCards: TrustCard[] = [
  {
    icon: BellRing,
    title: '기회',
    oneLiner: '조건 맞는 프로젝트 요청을 알림/추천',
    proof: '키워드 기반 맞춤 추천',
    isHighlighted: true,
  },
  {
    icon: FileText,
    title: '속도',
    oneLiner: '제안서 템플릿으로 5분 제출',
    proof: '반복 입력 없이 빠른 지원',
  },
  {
    icon: MessagesSquare,
    title: '관리',
    oneLiner: '채팅/상태로 협의·결정 과정 추적',
    proof: '진행 흐름을 한 곳에서 확인',
  },
]

interface PolicyCard {
  icon: LucideIcon
  title: string
  description: string
}

const policyCards: PolicyCard[] = [
  {
    icon: ShieldCheck,
    title: '포트폴리오 확인 기반 활성화',
    description: '프로필/포트폴리오 확인 여부를 바탕으로 추천 신뢰도를 높입니다.',
  },
  {
    icon: Clock3,
    title: '응답 기준(SLA) 준수율 기반 노출 조정',
    description: '응답 지연이 반복되는 경우 추천 우선순위가 점진적으로 조정됩니다.',
  },
  {
    icon: ListChecks,
    title: '제안/협의/결정 이력 투명하게 관리',
    description: '제안부터 협의, 최종 결정까지 핵심 이력을 한 화면에서 확인할 수 있습니다.',
  },
]

interface TrustAndTestimonialsProps {
  isVisible: boolean
}

export default function TrustAndTestimonials({ isVisible }: TrustAndTestimonialsProps) {
  return (
    <section
      className={`w-full bg-white px-4 py-16 sm:px-6 md:py-20 lg:px-8 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="mx-auto max-w-6xl">
        {/* 신뢰 지표 카드 */}
        <div className="mb-16">
          <div className="mb-12 text-center">
            <h2 className="mb-2 text-3xl font-bold text-gray-900 md:text-4xl">
              프리랜서가 얻는 가치
            </h2>
            <p className="mx-auto max-w-2xl text-base text-gray-600 md:text-lg">
              프로필 등록 후 바로 체감할 수 있는 핵심 베네핏
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {trustCards.map((card, index) => {
              const Icon = card.icon
              return (
                <Card
                  key={card.title}
                  className={`rounded-2xl border transition-all hover:shadow-lg ${
                    card.isHighlighted
                      ? 'border-2 border-[#06f] bg-blue-50/30 shadow-md'
                      : 'border-gray-200 bg-white shadow-sm hover:shadow-md'
                  } ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{
                    transitionDelay: isVisible ? `${index * 100}ms` : '0ms',
                  }}
                >
                  <CardContent className="p-6">
                    {card.isHighlighted && (
                      <div className="mb-3 flex justify-center">
                        <span className="inline-flex items-center rounded-full bg-[#06f] px-2.5 py-0.5 text-xs font-semibold text-white">
                          추천
                        </span>
                      </div>
                    )}
                    <div className="mb-4 flex justify-center">
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                          card.isHighlighted ? 'bg-[#06f]/10' : 'bg-blue-50'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${card.isHighlighted ? 'text-[#06f]' : 'text-[#06f]'}`} />
                      </div>
                    </div>
                    <h3 className="mb-2 text-lg font-bold text-gray-900">{card.title}</h3>
                    <p className="mb-2 text-sm font-medium leading-relaxed text-gray-700">
                      {card.oneLiner}
                    </p>
                    <p className="text-xs leading-relaxed text-gray-500">{card.proof}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>

        {/* 신뢰/정책 카드 */}
        <div>
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              신뢰/정책
            </h2>
            <p className="mx-auto max-w-2xl text-base text-gray-600 md:text-lg">
              한국식 의사결정에 맞춘 신뢰 장치로 제안 품질을 높입니다
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {policyCards.map((policy, index) => {
              const Icon = policy.icon
              return (
              <Card
                key={policy.title}
                className={`rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: isVisible ? `${(trustCards.length + index) * 100}ms` : '0ms',
                }}
              >
                <CardContent className="p-6">
                  <div className="mb-4">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
                      <Icon className="h-5 w-5 text-[#06f]" />
                    </div>
                  </div>
                  <h3 className="mb-2 text-base font-bold text-gray-900">{policy.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-700">{policy.description}</p>
                </CardContent>
              </Card>
            )})}
          </div>
        </div>
      </div>
    </section>
  )
}

